import { doc, getDoc, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';

export enum PTestCollection {
  PERSONALITY_D = 'personalityd',
  PERSONALITY_E = 'personalitye',
}

export type TagsConfig = {
  path: string;
  maxSelection?: number;
  allowCustom?: boolean;
  placeholder?: string;
}

export type PTestQuestion = {
  id?: string;
  questionNo: number;
  question: string;
  isFlipped: boolean;
  category: string;
}

export type PositionDescription = {
  id: string;
  founderId?: string;
  hiringCompany: string;
  jobTitle: string;
  location: string;
  workStyle: string;
  commitment: string;
  description: [
    {
      title: string;
      content: string;
    }
  ];
}

export const TAGS_CONFIGS = {
  PROJECT_CATEGORIES: {
    path: 'forms/project-categories',
    maxSelection: 3,
    allowCustom: false,
  },
  DEGREES: {
    path: 'forms/degrees',
    maxSelection: 3,
    placeholder: 'Add a Custom Skill',
  },
  DEV_DEGREES: {
    path: 'forms/dev_degrees',
    maxSelection: 3,
    placeholder: 'Add a Custom Skill',
  },
} as const;

export type TagType = keyof typeof TAGS_CONFIGS;

class TagsService {
  private cache = new Map<string, string[]>();
  private cacheExpiry = new Map<string, number>();
  private pTestCache = new Map<string, PTestQuestion[]>();
  private pTestCacheExpiry = new Map<string, number>();
  private projectImagesCache: string[] | null = null;
  private projectImagesCacheExpiry: number = 0;
  private jobRoleCache = new Map<string, PositionDescription>();
  private jobRoleCacheExpiry = new Map<string, number>();
  private readonly CACHE_DURATION = 60 * 60 * 1000; // 1 hour

  async getTags(config: TagsConfig): Promise<string[]> {
    const { path } = config;
    const now = Date.now();

    if (this.cache.has(path) && this.cacheExpiry.has(path)) {
      const expiry = this.cacheExpiry.get(path)!;
      if (now < expiry) {
        return this.cache.get(path)!;
      }
    }

    try {
      const docRef = doc(db, path);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const tags = this.parseTagsData(data);

        // Cache the result
        this.cache.set(path, tags);
        this.cacheExpiry.set(path, now + this.CACHE_DURATION);

        return tags;
      }

      return [];
    } catch (error) {
      return [];
    }
  }

  async getTagsByType(tagType: TagType): Promise<string[]> {
    const config = TAGS_CONFIGS[tagType];
    return this.getTags(config);
  }

  private parseTagsData(data: any): string[] {
    return data['display-tags'] || [];
  }

  async getPTestQuestions(collectionName: PTestCollection.PERSONALITY_D | PTestCollection.PERSONALITY_E): Promise<PTestQuestion[]> {
    const now = Date.now();

    if (this.pTestCache.has(collectionName) && this.pTestCacheExpiry.has(collectionName)) {
      const expiry = this.pTestCacheExpiry.get(collectionName)!;
      if (now < expiry) {
        return this.pTestCache.get(collectionName)!;
      }
    }

    try {
      const questionsRef = collection(db, collectionName);
      const questionsQuery = query(questionsRef, orderBy('questionNo', 'asc'));
      const querySnapshot = await getDocs(questionsQuery);

      const questions: PTestQuestion[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        questions.push({
          id: doc.id,
          questionNo: data.questionNo,
          question: data.question,
          isFlipped: data.isFlipped || false,
          category: data.category || '',
        });
      });

      // Cache the result
      this.pTestCache.set(collectionName, questions);
      this.pTestCacheExpiry.set(collectionName, now + this.CACHE_DURATION);

      return questions;
    } catch (error) {
      return [];
    }
  }

  async getProjectImages(): Promise<string[]> {
    const now = Date.now();

    // Check cache first
    if (this.projectImagesCache && now < this.projectImagesCacheExpiry) {
      return this.projectImagesCache;
    }

    try {
      // Create a reference to the projectImages folder
      const projectImagesRef = ref(storage, 'projectImages');

      // List all items in the folder
      const listResult = await listAll(projectImagesRef);

      // Get download URLs for all images
      const imageUrls = await Promise.all(
        listResult.items.map(async (imageRef) => {
          try {
            return await getDownloadURL(imageRef);
          } catch (error) {
            return null;
          }
        }),
      );

      // Filter out any failed URLs and cache the result
      const validUrls = imageUrls.filter((url): url is string => url !== null);
      this.projectImagesCache = validUrls;
      this.projectImagesCacheExpiry = now + this.CACHE_DURATION;

      return validUrls;
    } catch (error) {
      return [];
    }
  }

  async getJobRoleData(roleId: string): Promise<PositionDescription | null> {
    const now = Date.now();

    if (this.jobRoleCache.has(roleId) && this.jobRoleCacheExpiry.has(roleId)) {
      const expiry = this.jobRoleCacheExpiry.get(roleId)!;
      if (now < expiry) {
        return this.jobRoleCache.get(roleId)!;
      }
    }

    try {
      const documentId = roleId;

      // Fetch from Firestore collection
      const docRef = doc(db, 'job_roles_for_web_onboarding', documentId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();

      const roleData: PositionDescription = {
        id: roleId,
        founderId: data.founderId || '',
        hiringCompany: data.hiringCompany || '',
        jobTitle: data.jobTitle || '',
        location: data.location || '',
        workStyle: data.workStyle || '',
        commitment: data.commitment || '',
        description: data.description || [],
      };

      this.jobRoleCache.set(roleId, roleData);
      this.jobRoleCacheExpiry.set(roleId, now + this.CACHE_DURATION);

      return roleData;
    } catch (error) {
      return null;
    }
  }
}

// Export singleton instance
export const tagsService = new TagsService();

// tags
export const getProjectCategories = () => tagsService.getTagsByType('PROJECT_CATEGORIES');
export const getDegrees = () => tagsService.getTagsByType('DEGREES');
export const getDevDegrees = () => tagsService.getTagsByType('DEV_DEGREES');

// PTest
export const getPTestQuestions = (collectionName: PTestCollection.PERSONALITY_D | PTestCollection.PERSONALITY_E) =>
  tagsService.getPTestQuestions(collectionName);

// Project images
export const getProjectImages = () => tagsService.getProjectImages();

// Position description
export const getJobDescriptionData = (jobRoleId: string) => tagsService.getJobRoleData(jobRoleId);
